import React, { useState, useEffect } from 'react';
import UserCog from 'lucide-react/dist/esm/icons/user-cog';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import Upload from 'lucide-react/dist/esm/icons/upload';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Info from 'lucide-react/dist/esm/icons/info';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import UsersIcon from 'lucide-react/dist/esm/icons/users';
import ExternalLink from 'lucide-react/dist/esm/icons/external-link';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import FileImage from 'lucide-react/dist/esm/icons/file-image';
import FileType from 'lucide-react/dist/esm/icons/file-type';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import Eye from 'lucide-react/dist/esm/icons/eye';
import Download from 'lucide-react/dist/esm/icons/download';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { motion } from 'framer-motion';
import { Modal } from '../common/Modal';

interface PowerOfAttorney {
  id: string;
  type: 'financial' | 'medical';
  principal_name: string;
  principal_address: string;
  principal_phone: string;
  principal_email: string;
  agent_name: string;
  agent_address: string;
  agent_phone: string;
  agent_email: string;
  powers: string[];
  limitations: string[];
  effective_date: string;
  termination_date?: string;
  witness1_name: string;
  witness1_address: string;
  witness2_name: string;
  witness2_address: string;
  notary_name: string;
  notary_commission: string;
  notary_expiry: string;
  document_url?: string;
}

interface POADocument {
  id: string;
  user_id: string;
  title: string;
  type: string;
  status: string;
  content: string;
  file_path?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  file_url?: string;
  last_reviewed?: string;
  shared_with?: string[];
  created_at: string;
  updated_at: string;
  effective_date?: string;
  termination_date?: string;
}

// State-specific POA form links
const statePOALinks = [
  {
    state: 'NSW',
    name: 'New South Wales',
    financialPOA: 'https://www.service.nsw.gov.au/transaction/get-started-making-a-power-of-attorney',
    medicalPOA: 'https://www.nsw.gov.au/legal-and-justice/legal-forms/enduring-guardianship',
    description: 'NSW Trustee & Guardian forms and information'
  },
  {
    state: 'VIC',
    name: 'Victoria',
    financialPOA: 'https://www.publicadvocate.vic.gov.au/your-rights/enduring-power-of-attorney/making-a-power-of-attorney',
    medicalPOA: 'https://www.ptv.vic.gov.au/forms-and-publications/medical-treatment-decision-maker/',
    description: 'Public Trustee Victoria forms'
  },
  {
    state: 'QLD',
    name: 'Queensland',
    financialPOA: 'https://www.publications.qld.gov.au/dataset/power-of-attorney-and-advance-health-directive-forms',
    medicalPOA: 'https://www.publictrustee.qld.gov.au/forms-and-publications/advance-health-directive/',
    description: 'Public Trustee Queensland forms'
  },
  {
    state: 'WA',
    name: 'Western Australia',
    financialPOA: 'https://www.wa.gov.au/service/justice/civil-law/enduring-power-of-attorney',
    medicalPOA: 'https://www.wa.gov.au/organisation/department-of-justice/office-of-the-public-advocate/forms',
    description: 'Office of the Public Advocate WA forms'
  },
  {
    state: 'SA',
    name: 'South Australia',
    financialPOA: 'https://www.sa.gov.au/topics/family-and-community/planning-ahead/make-a-power-of-attorney',
    medicalPOA: 'https://www.ptv.sa.gov.au/forms-and-publications/advance-care-directive/',
    description: 'Public Trustee South Australia forms'
  },
  {
    state: 'TAS',
    name: 'Tasmania',
    financialPOA: 'https://www.publictrustee.tas.gov.au/enduring-power-of-attorney/',
    medicalPOA: 'https://www.publictrustee.tas.gov.au/forms-and-publications/enduring-guardianship/',
    description: 'Public Trustee Tasmania forms'
  },
  {
    state: 'ACT',
    name: 'Australian Capital Territory',
    financialPOA: 'https://www.ptg.act.gov.au/enduring-powers-of-attorney',
    medicalPOA: 'https://www.act.gov.au/our-canberra/law-and-justice/legal-forms/enduring-power-of-attorney',
    description: 'ACT Government legal forms'
  },
  {
    state: 'NT',
    name: 'Northern Territory',
    financialPOA: 'https://nt.gov.au/law/processes/power-of-attorney/how-to-make-a-general-power-of-attorney',
    medicalPOA: 'https://nt.gov.au/law/legal-forms/advance-personal-plan',
    description: 'NT Government legal forms'
  }
];

export function PowerOfAttorney() {
  const { user } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadType, setUploadType] = useState<'financial' | 'medical'>('financial');
  const [documents, setDocuments] = useState<POADocument[]>([]);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [shareForm, setShareForm] = useState({
    email: '',
    accessLevel: 'view',
    note: ''
  });

  const fetchDocuments = async (background = false) => {
    try {
      if (!background) setLoading(true);
      if (!user) return;
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'poa')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to fetch documents');
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) fetchDocuments(true); };
    window.addEventListener('refresh-legal', handler);
    return () => window.removeEventListener('refresh-legal', handler);
  }, [user]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload PDF, DOC, DOCX, or image files.');
      return;
    }
    try {
      setError(null);
      setUploading(true);
      const filePath = `${user?.id}/poa/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('legal-documents')
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      
      // Generate a signed URL for the uploaded file
      const { data: urlData, error: urlError } = await supabase.storage
        .from('legal-documents')
        .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry
      if (urlError) throw urlError;
      
      const { error: dbError } = await supabase
        .from('legal_documents')
        .insert([
          {
            user_id: user?.id,
            title: `Uploaded ${uploadType === 'financial' ? 'Financial' : 'Medical'} POA - ${file.name}`,
            type: 'poa',
            status: 'active',
            content: JSON.stringify({ file_path: filePath }),
            file_path: filePath,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            file_url: urlData.signedUrl,
            last_reviewed: new Date().toISOString(),
          },
        ]);
      if (dbError) throw dbError;
      setShowUploadModal(false);
      fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: POADocument) => {
    try {
      setDownloading(doc.id);
      const filePath = doc.file_path || JSON.parse(doc.content || '{}')?.file_path;
      if (!filePath) throw new Error('No file path found');
      const { data, error } = await supabase.storage
        .from('legal-documents')
        .download(filePath);
      if (error) throw error;
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (doc: POADocument) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) return;
    try {
      setDeleting(doc.id);
      const filePath = doc.file_path || JSON.parse(doc.content || '{}')?.file_path;
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('legal-documents')
          .remove([filePath]);
        if (storageError) throw storageError;
      }
      const { error: dbError } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', doc.id);
      if (dbError) throw dbError;
      fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const handleView = (doc: POADocument) => {
    const filePath = doc.file_path || JSON.parse(doc.content || '{}')?.file_path;
    if (filePath) {
      try {
        const { data } = supabase.storage
          .from('legal-documents')
          .getPublicUrl(filePath);
        if (data.publicUrl) {
          if (doc.file_type === 'application/pdf') {
            setPdfViewerUrl(data.publicUrl);
          } else {
            window.open(data.publicUrl, '_blank');
          }
        } else {
          setError('Unable to generate view URL for document');
        }
      } catch (err) {
        setError('Failed to generate view URL for document');
      }
    }
  };

  const handleShare = (doc: POADocument) => {
    setShareDocId(doc.id);
    setShareForm({ email: '', accessLevel: 'view', note: '' });
    setShowShareModal(true);
  };

  const handleShareFormChange = (field: string, value: string) => {
    setShareForm(prev => ({ ...prev, [field]: value }));
  };

  const handleShareSubmit = async () => {
    if (!shareDocId || !shareForm.email.trim()) return;
    try {
      const shareData = {
        email: shareForm.email.trim(),
        accessLevel: shareForm.accessLevel,
        note: shareForm.note.trim(),
        sharedAt: new Date().toISOString()
      };
      const currentDoc = documents.find(doc => doc.id === shareDocId);
      const currentShared = currentDoc?.shared_with || [];
      const newShared = [...currentShared, JSON.stringify(shareData)];
      const { error } = await supabase
        .from('legal_documents')
        .update({ shared_with: newShared })
        .eq('id', shareDocId);
      if (error) throw error;
      setShowShareModal(false);
      setShareDocId(null);
      setShareForm({ email: '', accessLevel: 'view', note: '' });
      fetchDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share document');
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType?.includes('pdf')) return <FileText size={32} className="text-red-500" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <FileType size={32} className="text-blue-500" />;
    if (fileType?.includes('image')) return <FileImage size={32} className="text-green-500" />;
    return <FileText size={32} className="text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-8">
      {/* Header & Quick Actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <UserCog className="text-blue-600" size={28} /> Power of Attorney
          </h2>
          <p className="text-gray-600 mt-1 max-w-xl">
            Access official Power of Attorney forms from your state government and manage your existing documents securely.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" /> Upload Existing
          </Button>
        </div>
      </div>

      {/* State Government POA Forms */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <MapPin className="text-blue-600" size={24} />
          <h3 className="text-xl font-semibold text-gray-900">State Government POA Forms</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Click on your state to access official Power of Attorney forms from your state government. These forms are legally recognized and should be completed with legal advice.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statePOALinks.map((state) => (
            <motion.div
              key={state.state}
              whileHover={{ scale: 1.02 }}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="text-center mb-3">
                <h4 className="font-semibold text-gray-900">{state.name}</h4>
                <p className="text-xs text-gray-500">{state.description}</p>
              </div>
              
              <div className="space-y-2">
                <a
                  href={state.financialPOA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center px-3 py-2 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="w-3 h-3 inline mr-1" />
                  Financial POA
                </a>
                <a
                  href={state.medicalPOA}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center px-3 py-2 bg-green-50 text-green-700 rounded-md hover:bg-green-100 transition-colors text-sm font-medium"
                >
                  <ExternalLink className="w-3 h-3 inline mr-1" />
                  Medical POA
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="flex items-center gap-4 p-4">
          <ShieldCheck className="text-green-600" size={28} />
          <div>
            <div className="font-semibold">Active POAs</div>
            <div className="text-2xl font-bold text-green-700">{documents.filter(d => d.effective_date && (!d.termination_date || new Date(d.termination_date) > new Date())).length}</div>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-4">
          <UsersIcon className="text-blue-600" size={28} />
          <div>
            <div className="font-semibold">Agents Assigned</div>
            <div className="text-2xl font-bold text-blue-700">{[...new Set(documents.map(d => d.type))].length}</div>
          </div>
        </Card>
        <Card className="flex items-center gap-4 p-4">
          <Info className="text-amber-600" size={28} />
          <div>
            <div className="font-semibold">Expiring Soon</div>
            <div className="text-2xl font-bold text-amber-700">{documents.filter(d => d.termination_date && new Date(d.termination_date) < new Date(Date.now() + 1000*60*60*24*30)).length}</div>
          </div>
        </Card>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <span className="animate-spin mr-2">🔄</span> Loading POA documents...
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      )}

      {/* Document List */}
      {!loading && (
        <>
          {documents.length === 0 ? (
            <Card className="p-8 text-center flex flex-col items-center gap-4">
              <UserCog className="text-blue-400 mx-auto" size={48} />
              <h3 className="text-xl font-semibold">No Power of Attorney documents yet</h3>
              <p className="text-gray-500 max-w-md mx-auto">Upload your existing POA documents to keep them organised and easily accessible. Use the state government forms above to create new documents.</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => setShowUploadModal(true)}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Document
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold mb-4">Your Documents</h2>
                <div className="space-y-4">
                  {documents.map((doc) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-between p-6 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg">
                          {getFileIcon(doc.file_type || '')}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">{doc.title}</h3>
                          <div className="flex items-center gap-6 text-sm text-gray-600 mt-1">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                              {doc.type === 'financial' ? 'Financial' : 'Medical'}
                            </span>
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                              {doc.status}
                            </span>
                            {doc.file_size && <span className="flex items-center gap-1"><FileText size={14} /> {formatFileSize(doc.file_size)}</span>}
                            {doc.last_reviewed && (
                              <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(doc.last_reviewed).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleView(doc)} 
                          disabled={deleting === doc.id}
                          className="flex items-center gap-2 min-w-[100px]"
                        >
                          <Eye size={18} />
                          {doc.file_type === 'application/pdf' ? 'View PDF' : 'View'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleDownload(doc)} 
                          disabled={deleting === doc.id || downloading === doc.id}
                          className="flex items-center gap-2 min-w-[120px]"
                        >
                          {downloading === doc.id ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                          ) : (
                            <Download size={18} />
                          )}
                          {downloading === doc.id ? 'Downloading...' : 'Download'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleShare(doc)} 
                          disabled={deleting === doc.id}
                          className="flex items-center gap-2 min-w-[100px]"
                        >
                          <Share2 size={18} />
                          Share
                        </Button>
                        <Button 
                          variant="outline" 
                          size="md" 
                          onClick={() => handleDelete(doc)} 
                          disabled={deleting === doc.id}
                          className="flex items-center gap-2 min-w-[100px] border-red-200 text-red-600 hover:bg-red-50"
                        >
                          {deleting === doc.id ? (
                            <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
                          ) : (
                            <Trash2 size={18} />
                          )}
                          {deleting === doc.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Tips & Resources */}
      <Card className="p-6 mt-8 bg-blue-50 border-blue-100">
        <div className="flex items-center gap-3 mb-2">
          <Info className="text-blue-600" />
          <span className="font-semibold text-blue-900">Important Information</span>
        </div>
        <ul className="list-disc pl-6 text-sm text-blue-900 space-y-1">
          <li>Always consult with a legal professional when creating Power of Attorney documents.</li>
          <li>State government forms are legally recognized and regularly updated.</li>
          <li>Review your POA documents annually or after major life events.</li>
          <li>Ensure your agent is someone you trust and keep their contact info up to date.</li>
          <li>Store your POA documents securely and share copies with relevant parties.</li>
        </ul>
      </Card>

      {/* PDF Viewer Modal */}
      <Modal 
        isOpen={!!pdfViewerUrl} 
        onClose={() => setPdfViewerUrl(null)} 
        title="Document Viewer"
        maxWidth="xl"
      >
        <div className="flex flex-col" style={{height: '70vh'}}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
                <FileText size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">PDF Document</h3>
                <p className="text-sm text-gray-500">Viewing your uploaded document</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(pdfViewerUrl!, '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink size={16} />
                Open in New Tab
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPdfViewerUrl(null)}
              >
                Close
              </Button>
            </div>
          </div>
          <div className="flex-1 bg-gray-100 rounded-lg overflow-auto">
            {pdfViewerUrl && (
              <iframe
                src={`${pdfViewerUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                className="w-full h-full border-0 rounded-lg"
                title="PDF Viewer"
                style={{minHeight: '400px'}}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Share Modal */}
      <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} title="Share Document">
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Share2 size={20} className="text-blue-600" />
              <h3 className="font-semibold text-blue-900">Share Document</h3>
            </div>
            <p className="text-sm text-blue-700">
              Share this document with someone by providing their email address and access level.
            </p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={shareForm.email}
                onChange={(e) => handleShareFormChange('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="recipient@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Level
              </label>
              <select
                value={shareForm.accessLevel}
                onChange={(e) => handleShareFormChange('accessLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="view">View Only</option>
                <option value="comment">View & Comment</option>
                <option value="edit">View & Edit</option>
                <option value="full">Full Access</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {shareForm.accessLevel === 'view' && 'Recipient can only view the document'}
                {shareForm.accessLevel === 'comment' && 'Recipient can view and add comments'}
                {shareForm.accessLevel === 'edit' && 'Recipient can view and edit the document'}
                {shareForm.accessLevel === 'full' && 'Recipient has full access including sharing'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note to Recipient (Optional)
              </label>
              <textarea
                value={shareForm.note}
                onChange={(e) => handleShareFormChange('note', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a personal message or instructions for the recipient..."
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button 
              variant="outline" 
              onClick={() => setShowShareModal(false)}
              size="md"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleShareSubmit}
              size="md"
              disabled={!shareForm.email.trim()}
              className="flex items-center gap-2"
            >
              <Share2 size={18} />
              Share Document
            </Button>
          </div>
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title={`Upload ${uploadType === 'financial' ? 'Financial' : 'Medical'} POA Document`}
      >
        <div className="space-y-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              id="file-upload-poa"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload-poa"
              className={`cursor-pointer flex flex-col items-center justify-center ${uploading ? 'opacity-50' : ''}`}
            >
              {uploading ? (
                <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-4" />
              ) : (
                <Upload size={40} className="text-gray-400 mb-4" />
              )}
              <p className="text-sm text-gray-600">
                {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, DOC, DOCX, or images (max. 50MB)
              </p>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
            <select
              value={uploadType}
              onChange={(e) => setUploadType(e.target.value as 'financial' | 'medical')}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={uploading}
            >
              <option value="financial">Financial Power of Attorney</option>
              <option value="medical">Medical Power of Attorney</option>
            </select>
          </div>
          {error && (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}